import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, X } from "lucide-react";

/**
 * Floating banner that surfaces a "new version available" prompt when
 * the service worker detects a fresh deploy. Gives the user control
 * over the reload moment instead of an automatic refresh that could
 * interrupt their work (typing a flyer description, mid-publish, …).
 *
 * Listens to the SW_UPDATED message dispatched by sw.js + the standard
 * `controllerchange` event. Stays dismissable for the current session.
 */
export default function NewVersionBanner() {
    const [show, setShow] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

        const onMessage = (event) => {
            if (event.data && event.data.type === "SW_UPDATED") {
                setShow(true);
            }
        };
        navigator.serviceWorker.addEventListener("message", onMessage);

        // Some browsers fire controllerchange before the message reaches
        // the React tree (slow paint, hydration race). Catching both
        // events guarantees the banner shows on every actual update.
        let already = false;
        const onControllerChange = () => {
            if (already) return;
            already = true;
            setShow(true);
        };
        navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

        return () => {
            navigator.serviceWorker.removeEventListener("message", onMessage);
            navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
        };
    }, []);

    if (!show || dismissed) return null;

    const reload = () => window.location.reload();
    const dismiss = () => setDismissed(true);

    return (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] px-4 max-w-md w-full"
             data-testid="new-version-banner">
            <div className="rounded-2xl bg-discover-gradient text-white shadow-2xl border border-white/20
                            px-4 py-3 flex items-center gap-3 backdrop-blur-sm">
                <RefreshCw size={18} className="shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">Nouvelle version disponible</div>
                    <div className="text-xs opacity-90 truncate">
                        Recharge pour voir les dernières améliorations.
                    </div>
                </div>
                <Button onClick={reload} size="sm"
                    variant="secondary"
                    className="rounded-full bg-white text-foreground font-semibold hover:bg-white/90"
                    data-testid="new-version-reload">
                    Recharger
                </Button>
                <button onClick={dismiss} aria-label="Plus tard"
                    className="text-white/70 hover:text-white"
                    data-testid="new-version-dismiss">
                    <X size={16} />
                </button>
            </div>
        </div>
    );
}
